import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { buildAuthRedirectToUrl } from '@om/shared-auth';
import type { SuggestionScreenshot } from '@om/shared-types';
import {
  Menu,
  X,
  ChevronDown,
  Bell,
  MessageCircle,
  HelpCircle
} from 'lucide-react';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { LanguagePicker } from '@om/shared-ui';
import { changeLanguage, LANGUAGE_OPTIONS } from '@om/i18n';
import type { LanguageCode } from '@om/i18n';
import { UserService } from '@/lib/api/user.service';
import { useAuth } from '@/hooks/useAuth';
import { useActiveCelebrations } from '@/hooks/useActiveCelebrations';
import { captureFeedbackScreenshot } from '@/lib/feedback/screenshot';
import { uploadService } from '@/lib/api/upload.service';
import { PersistentAIAssistant } from './PersistentAIAssistant';
import { PersistentNotifications } from './PersistentNotifications';
import { CelebrationRibbon } from './CelebrationRibbon';

interface LayoutProps {
  children?: React.ReactNode;
}

interface FeedbackNavigationState {
  prefilledScreenshots?: SuggestionScreenshot[];
  prefillErrorMessage?: string;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');
  const { user, logout, isAuthenticated } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState<LanguageCode>(
    (i18next.language?.split('-')[0] as LanguageCode) || 'en'
  );
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { activeCelebrations, loading: celebrationsLoading, canCreateNew } = useActiveCelebrations();

  const handleLanguageChange = (code: string) => {
    void changeLanguage(code as LanguageCode);
    setCurrentLanguage(code as LanguageCode);
    if (isAuthenticated) {
      // Fire-and-forget: persist to profile
      void UserService.updateProfile(0, {
        full_name: user?.full_name ?? '',
        email: user?.email ?? '',
        preferred_language: code,
      });
    }
  };

  const redirectToAuth = () => {
    window.location.assign(buildAuthRedirectToUrl(window.location.href));
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isUserMenuOpen]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, []);

  const handleLogout = async () => {
    try {
      setIsUserMenuOpen(false);
      await logout();
      navigate('/');
    } catch {
      navigate('/');
    }
  };

  const captureAndUploadFeedbackScreenshot = async (): Promise<SuggestionScreenshot> => {
    const file = await captureFeedbackScreenshot();
    const uploaded = await uploadService.uploadImage(file);

    return {
      url: uploaded.url,
      filename: uploaded.filename,
      original_name: file.name,
      size: file.size,
      mime_type: file.type,
    };
  };

  const navigateToFeedback = async (options?: { closeUserMenu?: boolean; closeMobileMenu?: boolean }) => {
    const wantsScreenshot = window.confirm('Do you want to attach a screenshot of the current tab to your feedback form?');
    const feedbackState: FeedbackNavigationState = {};

    if (wantsScreenshot) {
      try {
        const screenshot = await captureAndUploadFeedbackScreenshot();
        feedbackState.prefilledScreenshots = [screenshot];
      } catch (error: unknown) {
        feedbackState.prefillErrorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to capture and attach screenshot. You can still upload screenshots from the form.';
      }
    }

    if (options?.closeUserMenu) {
      setIsUserMenuOpen(false);
    }
    if (options?.closeMobileMenu) {
      setIsMobileMenuOpen(false);
    }

    navigate('/feedback', {
      state: Object.keys(feedbackState).length > 0 ? feedbackState : undefined,
    });
  };

  const isChatRoute = location.pathname === '/chat';
  const routeSearchParams = new URLSearchParams(location.search);
  // Only the chat-based creation flow needs edge-to-edge full-viewport scrolling;
  // the default chooser (no mode) is a normal scrollable page.
  const isCelebrationChatCreationRoute =
    location.pathname === '/celebrations/new' &&
    routeSearchParams.get('mode') === 'chat';
  const isFullViewportRoute = isChatRoute || isCelebrationChatCreationRoute;
  const homeRoute = isAuthenticated ? '/home' : '/';

  return (
    <div className={`${isFullViewportRoute ? 'h-screen overflow-hidden' : 'min-h-screen'} bg-background flex flex-col`}>
      {/* Compact Header */}
      <header className="bg-white border-b border-border sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 h-12 flex items-center justify-between">
          {/* Brand */}
          <div 
            className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate(homeRoute)}
          >
            <img src="/assets/curvato-logo.svg" alt="Curvato" className="w-8 h-8" />
            <span className="font-bold text-lg hidden sm:inline bg-clip-text text-transparent bg-gradient-to-r from-primary to-celebration-purple">
              curvato.us
            </span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6 mx-6">
            <button 
              onClick={() => navigate(homeRoute)}
              className={`text-sm font-medium transition-colors hover:text-primary ${
                location.pathname === '/home' ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              {t('nav.home')}
            </button>
            <button 
              onClick={() => navigate('/chat')}
              className={`text-sm font-medium transition-colors hover:text-primary ${
                location.pathname.startsWith('/chat') ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              {t('nav.chat')}
            </button>
            <button 
              onClick={() => navigate('/contacts')}
              className={`text-sm font-medium transition-colors hover:text-primary ${
                location.pathname.startsWith('/contacts') ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              {t('nav.contacts')}
            </button>
            <button
              onClick={() => { navigate('/how-to'); }}
              className={`text-sm font-medium transition-colors hover:text-primary ${
                location.pathname.startsWith('/how-to') ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              {t('nav.howTo')}
            </button>
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {/* Mobile Menu */}
            <button
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
              className="sm:hidden p-1.5 rounded-md text-muted-foreground hover:bg-muted transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>

            {isAuthenticated ? (
              <>
                {!isCelebrationChatCreationRoute && (
                  <button
                    className="relative p-1.5 rounded-md text-muted-foreground hover:bg-muted transition-colors"
                    aria-label="Notifications"
                  >
                    <Bell className="h-5 w-5" />
                    <span className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-destructive" />
                  </button>
                )}

                <button
                  onClick={() => {
                    void navigateToFeedback();
                  }}
                  className={`hidden md:inline-flex px-2.5 py-1 rounded-md border text-sm font-medium transition-colors hover:bg-muted ${
                    location.pathname.startsWith('/feedback')
                      ? 'text-primary border-primary/40'
                      : 'text-muted-foreground border-border'
                  }`}
                >
                  {t('nav.feedback')}
                </button>

                <LanguagePicker
                  currentLanguage={currentLanguage}
                  onLanguageChange={handleLanguageChange}
                  languages={LANGUAGE_OPTIONS}
                  className="hidden sm:block"
                />

                {/* User Menu */}
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setIsUserMenuOpen((prev) => !prev)}
                    className="flex items-center gap-1.5 px-2 py-1 rounded-md border text-sm hover:bg-muted transition-colors"
                  >
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                      {(user?.full_name || user?.username || '?').charAt(0)}
                    </div>
                    <span className="hidden sm:inline font-medium max-w-[100px] truncate">{user?.full_name?.split(' ')[0]}</span>
                    <ChevronDown className="h-3 w-3 text-muted-foreground" />
                  </button>

                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border py-1 z-50">
                      <button
                        onClick={() => { navigate('/profile'); setIsUserMenuOpen(false); }}
                        className="w-full px-3 py-1.5 text-left text-sm hover:bg-muted transition-colors"
                      >
                        {t('nav.profile')}
                      </button>
                      <button
                        onClick={() => { navigate('/celebrations'); setIsUserMenuOpen(false); }}
                        className="w-full px-3 py-1.5 text-left text-sm hover:bg-muted transition-colors"
                      >
                        {t('nav.celebrations')}
                      </button>
                      <hr className="my-1" />
                      <button
                        onClick={handleLogout}
                        className="w-full px-3 py-1.5 text-left text-sm text-destructive hover:bg-destructive/10 transition-colors"
                      >
                        {t('auth.logout')}
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <button
                  onClick={redirectToAuth}
                  className="px-3 py-1.5 text-sm font-medium border rounded-md hover:bg-muted transition-colors"
                >
                  {t('auth.logIn')}
                </button>
                <button
                  onClick={redirectToAuth}
                  className="px-3 py-1.5 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                >
                  {t('auth.signUp')}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Celebrations second line in header (desktop) */}
        {isAuthenticated && !location.pathname.startsWith('/home') && (
          <div className="hidden md:block border-t border-border bg-white">
            <div className="max-w-7xl mx-auto px-4 h-10 flex items-center justify-center">
              <CelebrationRibbon
                className="overflow-x-auto no-scrollbar py-1 w-full justify-center"
                activeCelebrations={activeCelebrations}
                loading={celebrationsLoading}
                canCreateNew={canCreateNew}
              />
            </div>
          </div>
        )}
      </header>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 bg-white sm:hidden">
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center gap-2">
              <img src="/assets/curvato-logo.svg" alt="Curvato" className="w-6 h-6" />
              <span className="font-bold text-lg bg-clip-text text-transparent bg-gradient-to-r from-primary to-celebration-purple">
                curvato.us
              </span>
            </div>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-1.5 rounded-md text-muted-foreground hover:bg-muted"
              aria-label="Close menu"
              title="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="p-4 space-y-2">
            <button
              onClick={() => { navigate(homeRoute); setIsMobileMenuOpen(false); }}
              className="w-full text-left px-3 py-2 rounded-md hover:bg-muted transition-colors"
            >
              {t('nav.home')}
            </button>
            <button
              onClick={() => { navigate('/chat'); setIsMobileMenuOpen(false); }}
              className="w-full text-left px-3 py-2 rounded-md hover:bg-muted transition-colors flex items-center gap-2"
            >
              <MessageCircle className="h-4 w-4" />
              {t('nav.chat')}
            </button>
            <button
              onClick={() => { navigate('/contacts'); setIsMobileMenuOpen(false); }}
              className="w-full text-left px-3 py-2 rounded-md hover:bg-muted transition-colors"
            >
              {t('nav.contacts')}
            </button>
            <button
              onClick={() => { navigate('/how-to'); setIsMobileMenuOpen(false); }}
              className="w-full text-left px-3 py-2 rounded-md hover:bg-muted transition-colors flex items-center gap-2"
            >
              <HelpCircle className="h-4 w-4" />
              {t('nav.howTo')}?
            </button>
            <button
              onClick={() => {
                void navigateToFeedback({ closeMobileMenu: true });
              }}
              className="w-full text-left px-3 py-2 rounded-md hover:bg-muted transition-colors"
            >
              {t('nav.feedback')}
            </button>
          </nav>
        </div>
      )}

      {!isChatRoute && !isCelebrationChatCreationRoute && (
        <>
          {/* AI Assistant */}
          <PersistentAIAssistant
            onEventCreated={(eventData) => {
              if (typeof eventData === 'object' && eventData !== null && 'id' in eventData) {
                const id = (eventData as { id: number | string }).id;
                if (id) {
                  navigate(`/celebrations/${id}`);
                }
              }
            }}
          />

          {/* Notifications Panel */}
          <PersistentNotifications />
        </>
      )}

      {/* Main Content */}
      <main className={`flex-1 ${isFullViewportRoute ? 'overflow-hidden' : ''}`}>
        {isFullViewportRoute ? (
          // Full-viewport routes render edge-to-edge without max-width shell
          children || <Outlet />
        ) : (
          <div className="max-w-7xl mx-auto px-4 py-4">
            {children || <Outlet />}
          </div>
        )}
      </main>
    </div>
  );
};
